"use client";

import React from "react";
import type { TabConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { App_Users } from "@/src/gql/graphql";
import type { User } from "@/lib/types/user";
import { getEntityDefinition } from "@/lib/entity-registry";
import {
  AdminUserDetailTabComponents,
  AdminUserInfoPanel,
} from "@/components/admin/users/detail/AdminUserDetailTabComponents";

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in the detail config can never silently fall through.
type AdminUserTabComponent = React.ComponentType<{ item: User }>;
const ADMIN_USER_TAB_COMPONENTS = AdminUserDetailTabComponents as Record<
  string,
  AdminUserTabComponent
>;

function resolveAdminUserTabComponent(
  tab: TabConfig<App_Users> | undefined
): AdminUserTabComponent {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && ADMIN_USER_TAB_COMPONENTS[custom]) {
    return ADMIN_USER_TAB_COMPONENTS[custom];
  }
  return ADMIN_USER_TAB_COMPONENTS.AdminUserOverviewDisplay;
}

// ============================================================================
// ADMIN USER DETAIL MODAL TABS HOOK
// ============================================================================
// Mirrors useCompanyDetailModalTabs: pulls tabs from the registry detail config
// and renders the primary area + info panel for the generic detail modal.
// ============================================================================

export interface UseAdminUserDetailModalTabsReturn {
  tabs: TabConfig<App_Users>[];
  renderPrimaryArea: (activeTab: string, user: App_Users) => React.ReactNode;
  renderInfoPanel: (user: App_Users) => React.ReactNode;
}

export function useAdminUserDetailModalTabs(): UseAdminUserDetailModalTabsReturn {
  const tabs = getEntityDefinition("admin_users").detailConfig
    .tabs as unknown as TabConfig<App_Users>[];

  const renderPrimaryArea = React.useCallback((activeTab: string, row: App_Users): React.ReactNode => {
    const user = row as unknown as User;
    const tabConfig = tabs.find((t) => t.id === activeTab) ?? tabs[0];
    const TabComponent = resolveAdminUserTabComponent(tabConfig);
    return <TabComponent item={user} />;
  }, [tabs]);

  const renderInfoPanel = React.useCallback((row: App_Users): React.ReactNode => (
    <AdminUserInfoPanel user={row as unknown as User} />
  ), []);

  return React.useMemo(
    () => ({
      tabs,
      renderPrimaryArea,
      renderInfoPanel,
    }),
    [tabs, renderPrimaryArea, renderInfoPanel],
  );
}
